package org.maplibre.compose.gljs

import org.jetbrains.skia.BackendTexture
import org.jetbrains.skia.DirectContext

private val browserWindow: dynamic = js("window")

private const val MAKE_GL_SYMBOL = "org_jetbrains_skia_DirectContext__1nMakeGL"
private const val RESET_SYMBOL = "org_jetbrains_skia_DirectContext__1nReset"

/** Every GL state bit, matching `DirectContext.resetGLAll`. */
private const val GL_STATES = 0xffff

/**
 * Reaches the `GrDirectContext` Compose renders with, by wrapping skiko's wasm context factory
 * before Compose builds its renderer. A second `DirectContext.makeGL()` corrupts Compose's
 * rendering, so the existing one has to be caught as it is created.
 *
 * TODO: retire this, and [org.maplibre.compose.browser.installMapLibreCompose] with it, once
 *   [JetBrains/skiko#1219](https://github.com/JetBrains/skiko/pull/1219) or an equivalent lands.
 */
internal object SkikoGpuBridge {
  /** Skia context pointer for each Emscripten renderer generation. */
  private val contextPointers = mutableMapOf<Int, Any>()

  /** Which mangled property this build keeps a managed object's native pointer in. */
  private var pointerField: String? = null

  private var managedPrototype: dynamic = null

  private var hookInstalled = false

  /**
   * Must run after skiko's wasm module has published its exports and before Compose builds its
   * renderer. Returns false when those exports were not there to wrap.
   */
  fun install(): Boolean {
    if (hookInstalled) return true
    val original = browserWindow[MAKE_GL_SYMBOL]
    if (original == null || original == undefined) return false
    browserWindow[MAKE_GL_SYMBOL] = {
      val pointer = original()
      EmscriptenGl.currentHandle()?.let { handle ->
        contextPointers[handle] = pointer.unsafeCast<Any>()
      }
      pointer
    }
    hookInstalled = true
    learnPointerField()
    return true
  }

  /**
   * Wraps Compose's [DirectContext] pointer using a managed object's prototype. Optimized builds
   * read the pointer field directly; development builds use its accessor. Compose owns the pointer.
   * Do not call instance methods or free the pointer through this wrapper.
   */
  fun directContext(context: EmscriptenGlContext): DirectContext? {
    val pointer = contextPointers[context.handle] ?: return null
    val field = pointerField ?: return null
    val prototype = managedPrototype ?: return null
    val stand: dynamic = js("Object").create(prototype)
    stand[field] = pointer
    return stand.unsafeCast<DirectContext>()
  }

  /** `DirectContext.resetGLAll()`, through the export, since that method needs a real instance. */
  fun resetGlState(context: EmscriptenGlContext) {
    val pointer = contextPointers[context.handle] ?: return
    val reset = browserWindow[RESET_SYMBOL]
    if (reset == null || reset == undefined) return
    reset(pointer, GL_STATES)
  }

  fun isReady(context: EmscriptenGlContext): Boolean =
    pointerField != null && contextPointers.containsKey(context.handle)

  fun diagnostic(context: EmscriptenGlContext): String =
    when {
      !hookInstalled -> "skiko's exports were not on the page when MapLibre Compose initialized"
      pointerField == null -> "skiko's native pointer field could not be identified in this build"
      !contextPointers.containsKey(context.handle) ->
        "Compose's Skia context was not captured for Emscripten handle ${context.handle}"
      else -> "ready"
    }

  /**
   * A wrong guess hands Skia a bogus address, which corrupts rendering rather than failing
   * outright, so ambiguity leaves this unset.
   */
  private fun learnPointerField() {
    if (pointerField != null) return
    val first = probe()
    val second = probe()
    try {
      val a: dynamic = first
      val b: dynamic = second
      val names = js("Object").getOwnPropertyNames(a).unsafeCast<Array<String>>()
      val candidates = names.filter { name ->
        val value = a[name]
        jsTypeOf(value) == "number" && value != 0 && value != b[name]
      }
      if (candidates.size == 1) {
        pointerField = candidates.single()
        managedPrototype = js("Object").getPrototypeOf(a)
      }
    } finally {
      first.close()
      second.close()
    }
  }

  private fun probe(): BackendTexture =
    BackendTexture.makeGL(
      width = 1,
      height = 1,
      isMipmapped = false,
      textureId = 0,
      textureTarget = GL_TEXTURE_2D,
      textureFormat = GL_RGBA8,
    )
}
